import React, { useCallback, useEffect, useState } from 'react';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import styled from 'styled-components';

import { getEditPagePath } from '@utils/getPath';
import showNav from '@utils/showNav';
import {
  updateMenuBg,
  updateWorldTabOrder,
} from '@store/firebase/world/update-world';

import { CollectionPath, ImainhomeDoc } from '@typings/collections.enum';
import EditHeader from './EditHeader';
import MenuOrderItem from './menuOrderItem';
import HistoryList from '../common/HistoryList';
import { IHistoryItem } from '@typings/pages/history.interface';
import ThemeOptions from '@template/ThemeOptions/ThemeOptions';
import {
  EditSection,
  EditSectionDescription,
  EditSectionTitle,
} from '@styles/common/EditPage';
import { addHistoryDoc } from '@store/firebase/history/update-history';
import { useRecoilState } from 'recoil';
import { themeSetState } from '@atom/setting/globalStyle';
import { themeBgFileListState } from '@atom/posting/EditPosting';
import Button from '@components/dataEntry/button/Button';
import RadioBox from '@components/dataEntry/radio/RadioBox';
import {
  HISTORY_LAYOUT_TYPE,
  HISTORY_LAYOUT_TYPE_VALUE,
  historyLayoutTypeList,
} from '@typings/history/historyLayoutType';
import ListDroppable from '@components/library/DnD/ListDroppable';
import useScrollTop from '@hooks/useScrollTop';

const Container = styled.div`
  padding: 80px 0;
  margin: 0 auto;
  width: 100%;
  max-width: 920px;
  height: 100%;
`;

const RadioGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const AddButtonGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

type Props = {};
function HistoryEditPage({}: Props) {
  showNav(false);
  const collectionPath = getEditPagePath();
  const [selectedFile, setSelectedBgFile] = useState<File | null>(null);

  //recoil
  const [themeSet, setThemeSet] = useRecoilState(themeSetState);
  const [bgFileList, setBgFileList] = useRecoilState(themeBgFileListState);
  //end - recoil

  const { data } = useCollection<IHistoryItem>(`${collectionPath}`, {
    listen: true,
    orderBy: ['order', 'asc'],
  });

  const { data: historyPage, set } = useDocument<{
    themeSet?: any;
    bgImgURL?: string;
    layoutType?: HISTORY_LAYOUT_TYPE_VALUE;
  }>(`${CollectionPath.MAINHOME}/${collectionPath}Page`, {
    listen: true,
  });

  const handleLayoutType = useCallback((type) => {
    set({ layoutType: type }, { merge: true });
  }, []);

  const filteredImpactList = data?.filter((item, i) => item.isImpact === true);

  const handleAddHistory = useCallback(
    (dir: 'left' | 'right') => {
      if (!data) return;
      addHistoryDoc({
        collectionPath,
        data: {
          title: '1000',
          subTitle: '타이틀',
          description: '설명',
          order: data.length,
          contents: '내용 없음',
          dir,
        },
        bgFileList,
      });
    },
    [collectionPath, data, themeSet, bgFileList],
  );

  useEffect(() => {
    async function updateImgFile() {
      if (selectedFile) {
        const fileList = {
          selectedFile,
          prevImgURL: historyPage?.bgImgURL || '',
        };
        updateMenuBg({ fileList, docId: `${collectionPath}Page` });
      }
    }

    updateImgFile().then(() => {
      setSelectedBgFile(null);
    });
  }, [selectedFile, historyPage]);
  useScrollTop({ data, resetOnDataLoad: false });

  return (
    <>
      <EditHeader />
      <Container>
        <EditSection>
          <EditSectionTitle>상단 메뉴 편집</EditSectionTitle>
          <EditSectionDescription>
            '강조'선택 된 메뉴가 상단에 등록되어 클릭 시 해당 연표로 스크롤
            이동합니다.
          </EditSectionDescription>

          <ListDroppable
            list={filteredImpactList}
            handleUpdateOrder={updateWorldTabOrder}
            collectionPath={collectionPath}
          >
            <MenuOrderItem />
          </ListDroppable>
        </EditSection>
        <EditSection>
          <EditSectionTitle>레이아웃 선택</EditSectionTitle>
          <RadioGroup>
            {historyLayoutTypeList.map((type, i) => (
              <RadioBox
                key={i}
                value={type.value}
                id={`historyLayoutGroup-${type.key}`}
                name="historyLayoutGroup"
                checked={
                  historyPage && historyPage.layoutType
                    ? historyPage.layoutType === type.key
                    : type.key === HISTORY_LAYOUT_TYPE.VERTICAL
                }
                onChange={() => handleLayoutType(type.key)}
                shape="box"
              >
                <span>{type.value}</span>
              </RadioBox>
            ))}
          </RadioGroup>
        </EditSection>

        <EditSection>
          <EditSectionTitle>디자인 설정</EditSectionTitle>
          <ThemeOptions
            data={historyPage?.themeSet || {}}
            collectionPath={CollectionPath.MAINHOME}
            docId={ImainhomeDoc.HISTORYPAGE || ''}
            options={{
              themeColors: true,
              bgm: true,
              bgImg: true,
              setCustomTheme: true,
              font: true,
            }}
          />
        </EditSection>

        <EditSection>
          <EditSectionTitle>내용 편집</EditSectionTitle>
          <AddButtonGroup>
            <Button shape="stroke" onClick={() => handleAddHistory('left')}>
              연표 추가 (왼쪽)
            </Button>
            <Button shape="stroke" onClick={() => handleAddHistory('right')}>
              연표 추가 (오른쪽)
            </Button>
          </AddButtonGroup>
          <HistoryList isEdit={true} />
        </EditSection>
      </Container>
    </>
  );
}

export default HistoryEditPage;
